import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const jobs = [
  {
    dates: "Nov 2024 – Present",
    role: "Junior Engineer",
    org: "BART (Bay Area Rapid Transit)",
    place: "Oakland, CA",
    points: [
      <>Built a <span className="text-cream">verified history</span> of train false-occupancy events, cases where the signaling system reports a train on an empty track, by combining equipment logs, work orders, and manager notes, then cross-checking every date, location, and incident detail to resolve duplicates and conflicts. My SQL and Python analysis identified <span className="text-cream">low receiver thresholds (~32.7%)</span> and <span className="text-cream">data-bit mismatch (~30%)</span> as the leading cause groups, with <span className="text-cream">wiring issues (~15%)</span> as another contributing cause.</>,
      <>Analyzed passage logs, AI-camera detections, and field observations at three case-study stations to pinpoint where and when fare piggybacking concentrates. Those insights informed fare-gate software changes credited with a <span className="text-cream">30% decline in observed fare evasion</span>, with improvements still rolling out. Found ADA gates drove <span className="text-cream">~40% of incidents</span> and built Power BI dashboards to target mitigation pilots.</>,
      <>Extending my internship prototype, the <span className="text-cream">fully offline RAG system</span>, into a full <span className="text-cream">Elasticsearch-based platform</span> that ingests work orders and manuals, generates embeddings, and retrieves source-backed historical fixes, so technicians can troubleshoot faster in their day-to-day work. Designed for secure on-premises and hosted deployment.</>,
    ],
  },
  {
    dates: "Jun 2024 – Aug 2024",
    role: "Computer Science Intern",
    org: "BART (Bay Area Rapid Transit)",
    place: "Oakland, CA",
    caseStudy: "rag-assistant",
    points: [
      <>Engineered a <span className="text-cream">fully offline RAG maintenance assistant</span> in Python: text chunking, embeddings, FAISS similarity search, and local open-source LLMs indexing <span className="text-cream">five years of work orders and technical manuals</span>, with zero sensitive operational data exposed to cloud services.</>,
      <>Designed the <span className="text-cream">end-to-end retrieval and ranking workflow</span> so technicians could describe issues in natural language and receive grounded guidance from prior fixes and source manuals instead of keyword-only results.</>,
      <>Built the Streamlit interface and partnered with maintenance staff to <span className="text-cream">validate recommendation quality</span>, refine retrieval, and improve usability.</>,
    ],
  },
  {
    dates: "Jan 2024 – Apr 2024",
    role: "Data Science Program Participant",
    org: "Massachusetts Institute of Technology",
    place: "Cambridge, MA",
    points: [
      <>Completed a four-month, industry-mentored program with end-to-end projects in forecasting, computer vision, and classification, cleaning and modeling <span className="text-cream">10+ GB of data</span> with Python, SQL, Pandas, Scikit-learn, and TensorFlow.</>,
      <>Built end-to-end projects across the program: an ARIMA <span className="text-cream">sales forecast reaching 92% accuracy</span>, a TensorFlow <span className="text-cream">malaria-detection CNN at 85% precision</span>, handwritten digit recognition, a movie recommendation system, geographic clustering, and market analysis.</>,
    ],
  },
  {
    dates: "Jan 2022 – Apr 2023",
    role: "Student Bioinformatics Researcher",
    org: "Loyola Marymount University",
    place: "Los Angeles, CA",
    points: [
      <>Contributed to GRNsight, an open-source bioinformatics tool that <span className="text-cream">visualizes gene regulatory networks, showing how genes and proteins affect one another</span>, by debugging front-end behavior and tracing PostgreSQL-backed gene-network data so those relationships rendered accurately in interactive visualizations.</>,
      <>Presented the work to <span className="text-cream">200+ attendees</span> at LMU's annual research symposium, showing other departments how data and technology can unlock real value in biological research and <span className="text-cream">bridging computer science and biology</span> in a way audiences from both fields could act on.</>,
    ],
  },
];

export default function Experience() {
  return (
    <section id="experience" data-testid="experience-section" className="py-28 md:py-40">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="font-mono text-xs uppercase tracking-[0.35em] text-lime mb-6"
        >
          [ 02 ] · Experience
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-cream mb-20"
        >
          Where I've <span className="italic text-lime">worked</span>
        </motion.h2>

        <div className="relative border-l border-treeline ml-2 md:ml-6">
          {jobs.map((job, i) => (
            <motion.article
              key={job.role + i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: 0.05 * i, ease: EASE }}
              className="relative pl-10 md:pl-16 pb-20 last:pb-0 group"
              data-testid={`experience-item-${i}`}
            >
              <span className="absolute -left-[5px] top-2 h-2.5 w-2.5 bg-forest border border-lime rotate-45 group-hover:bg-lime transition-colors duration-300" />
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-lime">{job.dates}</p>
              <h3 className="mt-3 font-display text-2xl sm:text-3xl text-cream group-hover:text-lime transition-colors duration-300">
                {job.role}
              </h3>
              <p className="mt-1 font-mono text-sm text-sage">
                {job.org} · {job.place}
              </p>
              <ul className="mt-6 space-y-4 max-w-3xl">
                {job.points.map((p, j) => (
                  <li key={j} className="flex gap-4 font-mono text-sm leading-relaxed text-sage">
                    <span className="text-lime shrink-0">→</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              {job.caseStudy && (
                <Link
                  to={`/case-study/${job.caseStudy}`}
                  data-testid="experience-case-study-link"
                  className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] border border-lime/50 text-lime px-4 py-2 hover:bg-lime hover:text-forest transition-colors duration-300"
                >
                  Read the full case study <ArrowUpRight size={12} />
                </Link>
              )}
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
